import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import {
  fetchReleaseSubscriptions,
  addReleaseSubscribers,
  resendReleaseSubscription,
  removeReleaseSubscription,
  type ReleaseSubscription,
} from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { AgGridTable } from '../utiluicomponents/aggridtable';

export interface ReleaseNotesSubscribersPanelProps {
  /** Pre-fills the "Add subscribers" customer field; also included as a filter option. */
  readonly customer: string;
  readonly tenantId?: string;
  /** Bearer token for these calls (the app's own token). */
  readonly token?: string;
  /** Overrides `CTY_IDP_API_URL` for these calls. */
  readonly baseUrl?: string;
  readonly onBack: () => void;
}

const STATUS_CHIP_CLASS: Record<string, string> = {
  pending: 'bg-warning-100 text-warning-700',
  confirmed: 'bg-success-100 text-success-700',
  unsubscribed: 'bg-default-100 text-default-600',
};

type InlineMessage = { kind: 'success' | 'warning' | 'error'; text: string };

const INLINE_MESSAGE_CLASS: Record<InlineMessage['kind'], string> = {
  success: 'bg-success-50 text-success-700 border-success-200',
  warning: 'bg-warning-50 text-warning-700 border-warning-200',
  error: 'bg-danger-50 text-danger-700 border-danger-200',
};

const INLINE_MESSAGE_ICON: Record<InlineMessage['kind'], string> = {
  success: 'mdi:check-circle-outline',
  warning: 'mdi:alert-circle-outline',
  error: 'mdi:close-circle-outline',
};

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseEmailList(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter((email) => email.includes('@'));
}

export function ReleaseNotesSubscribersPanel(props: ReleaseNotesSubscribersPanelProps) {
  const { customer, tenantId, token, baseUrl, onBack } = props;
  const { t } = useTranslation();

  const [rows, setRows] = useState<ReleaseSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('');
  const [formCustomer, setFormCustomer] = useState(customer ?? '');
  const [emails, setEmails] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [message, setMessage] = useState<InlineMessage | null>(null);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchReleaseSubscriptions({ customer: customerFilter || undefined, token, baseUrl });
      setRows(data);
    } catch (error) {
      setMessage({ kind: 'error', text: t('Failed to load subscribers') });
      console.error('[ReleaseNotesSubscribersPanel] load failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [customerFilter, token, baseUrl, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const customers = useMemo(() => {
    const set = new Set<string>();
    if (customer) set.add(customer);
    rows.forEach((row) => row.customer && set.add(row.customer));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows, customer]);

  const handleAdd = async () => {
    const trimmedCustomer = formCustomer.trim();
    const list = parseEmailList(emails);
    if (!trimmedCustomer) {
      setMessage({ kind: 'warning', text: t('Pick a customer.') });
      return;
    }
    if (list.length === 0) {
      setMessage({ kind: 'warning', text: t('Enter at least one email.') });
      return;
    }
    setIsAdding(true);
    try {
      await addReleaseSubscribers({ customer: trimmedCustomer, emails: list, tenantId, token, baseUrl });
      setEmails('');
      setMessage({ kind: 'success', text: t('Subscriber(s) added — confirmation email sent.') });
      await load();
    } catch (error) {
      setMessage({ kind: 'error', text: t('Failed to add subscribers') });
      console.error('[ReleaseNotesSubscribersPanel] add failed:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleResend = async (row: ReleaseSubscription) => {
    setActioningId(row.id);
    try {
      await resendReleaseSubscription({ id: row.id, token, baseUrl });
      setMessage({ kind: 'success', text: t('Confirmation re-sent.') });
      await load();
    } catch (error) {
      setMessage({ kind: 'error', text: t('Failed to resend confirmation') });
      console.error('[ReleaseNotesSubscribersPanel] resend failed:', error);
    } finally {
      setActioningId(null);
    }
  };

  const handleRemove = async (row: ReleaseSubscription) => {
    if (!window.confirm(`${t('Remove')} ${row.email}?`)) return;
    setActioningId(row.id);
    try {
      await removeReleaseSubscription({ id: row.id, token, baseUrl });
      setMessage({ kind: 'success', text: t('Subscriber removed.') });
      await load();
    } catch (error) {
      setMessage({ kind: 'error', text: t('Failed to remove subscriber') });
      console.error('[ReleaseNotesSubscribersPanel] remove failed:', error);
    } finally {
      setActioningId(null);
    }
  };

  const StatusCellRenderer = useCallback((params: ICellRendererParams<ReleaseSubscription>) => {
    const value = String(params.value ?? '');
    return (
      <div className="flex items-center h-full">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_CHIP_CLASS[value] ?? 'bg-default-100 text-default-600'}`}>
          {value}
        </span>
      </div>
    );
  }, []);

  const ActionsCellRenderer = useCallback((params: ICellRendererParams<ReleaseSubscription>) => {
    const row = params.data;
    if (!row) return null;
    const isBusy = actioningId === row.id;
    return (
      <div className="flex items-center h-full justify-end gap-1">
        {row.status !== 'confirmed' ? (
          <CtyButton size="sm" variant="light" isDisabled={isBusy} isLoading={isBusy} onPress={() => void handleResend(row)}>
            {t('Resend')}
          </CtyButton>
        ) : null}
        <CtyButton size="sm" variant="light" color="danger" isDisabled={isBusy} onPress={() => void handleRemove(row)}>
          {t('Remove')}
        </CtyButton>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actioningId]);

  const columnDefs: ColDef<ReleaseSubscription>[] = useMemo(
    () => [
      { field: 'customer', headerName: t('CUSTOMER').toString(), flex: 1, minWidth: 140 },
      { field: 'email', headerName: t('EMAIL').toString(), flex: 2, minWidth: 220 },
      { field: 'status', headerName: t('STATUS').toString(), cellRenderer: StatusCellRenderer, flex: 1, minWidth: 120 },
      {
        field: 'created_at',
        headerName: t('ADDED').toString(),
        flex: 1,
        minWidth: 160,
        valueFormatter: (p: { value?: string }) => formatDateTime(p.value),
      },
      { headerName: '', cellRenderer: ActionsCellRenderer, flex: 1, minWidth: 170, sortable: false, filter: false },
    ],
    [t, StatusCellRenderer, ActionsCellRenderer]
  );

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto pr-1">
      <div className="flex items-center gap-3">
        <CtyButton isIconOnly variant="light" size="sm" onPress={onBack} aria-label={t('Back to releases').toString()}>
          <Icon icon="mdi:arrow-left" width="1.25em" height="1.25em" />
        </CtyButton>
        <div>
          <h2 className="text-lg font-semibold">{t('Release-notes subscribers')}</h2>
          <p className="text-xs text-default-500">
            {t('Add customer contacts to receive release-note emails. Each gets a confirmation link — only confirmed contacts are emailed.')}
          </p>
        </div>
      </div>

      <div className="border rounded-md p-4 flex flex-col gap-3">
        {message ? (
          <div
            role="alert"
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${INLINE_MESSAGE_CLASS[message.kind]}`}
          >
            <Icon icon={INLINE_MESSAGE_ICON[message.kind]} width="1.1em" height="1.1em" className="flex-shrink-0" />
            <span className="flex-1">{message.text}</span>
            <button
              type="button"
              onClick={() => setMessage(null)}
              aria-label={t('Dismiss').toString()}
              className="flex-shrink-0 opacity-70 hover:opacity-100"
            >
              <Icon icon="mdi:close" width="1em" height="1em" />
            </button>
          </div>
        ) : null}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="sm:w-56 sm:self-start">
            <label className="mb-1 block text-xs text-default-500">{t('Customer')}</label>
            <input
              list="release-notes-subscriber-customers"
              value={formCustomer}
              onChange={(e) => setFormCustomer(e.target.value)}
              placeholder={t('e.g. Endurance').toString()}
              className="w-full rounded-lg border border-default-200 bg-transparent px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
            <datalist id="release-notes-subscriber-customers">
              {customers.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs text-default-500">{t('Emails (comma or newline separated)')}</label>
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              rows={2}
              placeholder="alice@customer.com, bob@customer.com"
              className="w-full rounded-lg border border-default-200 bg-transparent px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <CtyButton
            color="primary"
            isLoading={isAdding}
            onPress={() => void handleAdd()}
            startContent={<Icon icon="mdi:email-plus-outline" width="1em" height="1em" />}
          >
            {t('Add & send confirm')}
          </CtyButton>
        </div>
      </div>

      <div className="flex flex-col gap-2 flex-1 min-h-0">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-sm">
            {t('Subscribers')} ({rows.length})
          </span>
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            aria-label={t('Filter by customer').toString()}
            className="rounded-lg border border-default-200 bg-transparent px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
          >
            <option value="">{t('All customers')}</option>
            {customers.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-h-[300px]">
          <AgGridTable tableData={rows} tableColumnDefs={columnDefs} isLoading={isLoading} disableRowClick />
        </div>
      </div>
    </div>
  );
}

export default ReleaseNotesSubscribersPanel;
